import { AlertTriangle } from 'lucide-react'
import { errorEntries } from '@/lib/errors'
import styles from './FormErrorSummary.module.scss'

export interface FormErrorSummaryProps {
  /** The error object (usually ``mutation.error`` from TanStack Query). */
  error: unknown
  /**
   * Map internal field paths (``config.url``) to human-readable labels
   * ("Link do produto"). Unknown fields fall back to the raw path. Pass
   * ``{}`` to show the raw field name.
   */
  fieldLabels?: Record<string, string>
  /** Title above the error list. Defaults to a generic PT-BR message. */
  title?: string
}

/**
 * Banner that surfaces a mutation/query error in a way the user can act on.
 * For 422 responses from the backend, it lists each invalid field with a
 * translated message. For everything else it falls back to a single line
 * so the form still has a visible failure signal.
 *
 * Stays quiet when ``error`` is nullish — safe to render unconditionally
 * next to a mutation, the component handles the "no error" case.
 */
export function FormErrorSummary({ error, fieldLabels = {}, title }: FormErrorSummaryProps) {
  if (error === null || error === undefined) return null
  const entries = errorEntries(error)
  if (entries.length === 0) return null

  // Show the headline only when there are multiple lines or at least one of
  // them is field-scoped; a single top-level error reads cleaner with just
  // the inline item text.
  const hasFieldScope = entries.some((e) => e.field !== null)
  const showHeader = entries.length > 1 || hasFieldScope

  return (
    <div className={styles.shell} role="alert" aria-live="polite">
      {showHeader ? (
        <div className={styles.header}>
          <AlertTriangle aria-hidden /> {title ?? 'Verifique os campos abaixo'}
        </div>
      ) : null}
      <ul className={styles.list}>
        {entries.map((entry, idx) => (
          <li key={`${entry.field ?? 'root'}-${String(idx)}`} className={styles.item}>
            {entry.field ? (
              <span className={styles.field}>{fieldLabels[entry.field] ?? entry.field}:</span>
            ) : null}
            <span>{entry.message}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
